import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box} from '@mui/material';
import {getMarkerTheme} from './markerTheme';
import {useReferenceMarkData} from '../../../../hooks/useReferenceMarkData';
import {useReferenceMarkerActions} from './hooks/useReferenceMarkerActions';
import {getIconColorFromSummary} from './utils/getIconColorFromSummary';
import ReferenceMarkerHeader from './ReferenceMarkerHeader';
import ReferenceMarkerTabs from './ReferenceMarkerTabs';
import AddedReferencesTab from './AddedReferencesTab';
import SuggestedReferencesTabContainer from './SuggestedReferencesTabContainer';
import ClaimsTabContainer from './ClaimsTabContainer';
import useYDocStore from '../../../../stores/useYDocStore';
import {requestClaimsExtraction} from '../../yMaps/claimsYMap';

const HEADER_FIELDS = {fields: ['references', 'claims']};

export default function ReferenceMarker({marker, isActive, onClick, reportId, onDelete, view}) {
    const [activeTab, setActiveTab] = useState(0);
    const [suggestedTotalCount, setSuggestedTotalCount] = useState(0);
    const extractionRequestedRef = useRef(false);
    const ydoc = useYDocStore(state => state.ydoc);
    const yReferenceMarks = useYDocStore(state => state.yReferenceMarks);

    const markData = useReferenceMarkData(marker?.uid, isActive ? undefined : HEADER_FIELDS);
    const {
        references,
        count,
        summary,
        status: claimsStatus,
        claims = [],
        analysisLoading,
        suggestionsLoading,
        proofInsightsLoading,
        contradictions = [],
    } = markData;

    const {
        sortedReferences,
        getReferenceNumber,
        handleAddSuggestedReference,
        handleRemoveSuggestedReference,
        handleFindMoreExamples,
        handleDeleteReference,
        handleReplaceWithAlternative,
        handleAddAlternativeAlongside,
    } = useReferenceMarkerActions({
        markerUid: marker?.uid,
        fullContent: marker?.fullContent,
        references,
        reportId,
        view,
    });

    useEffect(() => {
        if (!isActive || !marker?.uid) {
            extractionRequestedRef.current = false;
            return;
        }

        const needsExtraction =
            (claimsStatus === 'idle' || claimsStatus === 'error')
            && claims.length === 0
            && !analysisLoading;

        if (!needsExtraction) {
            if (claimsStatus !== 'idle' && claimsStatus !== 'error') {
                extractionRequestedRef.current = false;
            }
            return;
        }

        if (extractionRequestedRef.current) return;
        extractionRequestedRef.current = true;
        requestClaimsExtraction(ydoc, yReferenceMarks, marker.uid);
    }, [
        isActive,
        marker?.uid,
        claimsStatus,
        claims.length,
        analysisLoading,
        ydoc,
        yReferenceMarks,
    ]);

    const cfg = useMemo(() => getMarkerTheme('reference'), []);
    const {iconColor, iconBg, iconBorder} = useMemo(
        () => getIconColorFromSummary(summary, cfg, {contradictionCount: contradictions.length}),
        [summary, cfg, contradictions.length],
    );

    return (
        <Box
            className="pm-margin-marker pm-margin-marker--expanded"
            sx={{
                width: '100%',
                minHeight: 40,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: isActive ? '1px solid rgba(66,133,244,0.55)' : '1px solid transparent',
                boxShadow: isActive ? '0 6px 18px rgba(0,0,0,0.18)' : '0 0px 0px rgba(0,0,0,0)',
                userSelect: 'none',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                zIndex: isActive ? 999 : 'auto',
                ...(!isActive && {
                    '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.04)',
                    },
                }),
            }}
        >
            <ReferenceMarkerHeader
                marker={marker}
                isActive={isActive}
                onClick={onClick}
                onDelete={onDelete}
                count={count}
                analysisLoading={analysisLoading || proofInsightsLoading}
                claimsSummary={summary}
                iconColor={iconColor}
                iconBg={iconBg}
                iconBorder={iconBorder}
                Icon={cfg.Icon}
            />

            {isActive && (
                <Box sx={{width: '100%'}}>
                    <ReferenceMarkerTabs
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        sortedReferencesCount={sortedReferences.length}
                        suggestedCitationsCount={suggestedTotalCount}
                        suggestionsLoading={suggestionsLoading}
                        analysisLoading={analysisLoading}
                        claimsInsightsLoading={proofInsightsLoading}
                        claimsSummary={summary}
                    />

                    <Box sx={{maxHeight: 240, overflowY: 'auto'}}>
                        {activeTab === 0 && (
                            <AddedReferencesTab
                                sortedReferences={sortedReferences}
                                reportId={reportId}
                                statementText={marker?.fullContent}
                                getReferenceNumber={getReferenceNumber}
                                cfg={cfg}
                                onDeleteReference={handleDeleteReference}
                                onReplaceWithAlternative={handleReplaceWithAlternative}
                                onAddAlternativeAlongside={handleAddAlternativeAlongside}
                            />
                        )}

                        <Box sx={{display: activeTab === 1 ? 'block' : 'none'}}>
                            <SuggestedReferencesTabContainer
                                referenceMarkId={marker?.uid}
                                cfg={cfg}
                                onAddReference={handleAddSuggestedReference}
                                onRemoveReference={handleRemoveSuggestedReference}
                                onCountChange={setSuggestedTotalCount}
                            />
                        </Box>

                        {activeTab === 2 && (
                            <ClaimsTabContainer referenceMarkId={marker?.uid} />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
